/**
 * API client.
 *
 * Two responsibilities worth calling out:
 *
 *  1. The access token lives in module memory, never localStorage. An XSS bug
 *     is then unable to read it, and the refresh token is httpOnly so script
 *     cannot reach that either.
 *
 *  2. A 401 triggers exactly one refresh attempt, and concurrent requests that
 *     all 401 at once share that single attempt rather than stampeding the
 *     refresh endpoint (which would trip reuse detection and log the user out).
 */

import { isApiErrorBody, type ApiErrorBody, type AuthSession, type ErrorCode } from '@cenox/shared';

const BASE = '/api/v1';
const REFRESH_PATH = '/auth/refresh';

let accessToken: string | null = null;
let refreshInFlight: Promise<AuthSession | null> | null = null;
let onUnauthenticated: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Called when refreshing fails — the app routes back to sign-in. */
let onWorkspaceLost: ((businessId: string) => void) | null = null;

/**
 * Called when a business the browser is working in stops existing for this
 * person — they were removed from it, or it was deleted.
 *
 * The server's answer for that is the tenant gate's 404 "Business not found"
 * on the business's own paths. Without acting on it, a removed member's open
 * tab would keep that business selected and fail every request, forever.
 */
export function setWorkspaceLostHandler(handler: ((businessId: string) => void) | null): void {
  onWorkspaceLost = handler;
}

const TENANT_PATH = /^\/businesses\/([0-9a-f-]{36})(?:\/|$|\?)/i;

export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  onUnauthenticated = handler;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  readonly fields: Record<string, string[]> | undefined;
  readonly requestId: string | undefined;

  constructor(status: number, body: ApiErrorBody['error']) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.fields = body.fields;
    this.requestId = body.requestId;
  }

  /** First message for a given field, for inline form errors. */
  fieldError(name: string): string | undefined {
    return this.fields?.[name]?.[0];
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /**
   * Sent as-is, with no JSON encoding and no content-type of our own — the
   * browser has to set the multipart boundary itself, and a content-type we
   * write by hand would omit it and make the body unparseable.
   */
  formData?: FormData;
  signal?: AbortSignal;
}

async function rawRequest(path: string, options: RequestOptions = {}): Promise<Response> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (accessToken) headers.authorization = `Bearer ${accessToken}`;

  return fetch(`${BASE}${path}`, {
    method: options.method ?? 'GET',
    headers,
    // Required for the httpOnly refresh cookie.
    credentials: 'include',
    ...(options.formData !== undefined ? { body: options.formData } : {}),
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });
}

/**
 * The two clocks on a cross-tab refresh. Read at call time, so a test can
 * shorten them; nothing in the app changes them.
 *
 * `lockWaitMs` — how long a tab waits for another tab's refresh before giving
 * up on its own. Longer than `requestTimeoutMs`, so a tab queued behind a
 * hung refresh still gets its turn once that one is cancelled.
 *
 * `requestTimeoutMs` — how long one /auth/refresh may run, while holding the
 * lock, before it is aborted.
 */
export const REFRESH_TIMING = { lockWaitMs: 25_000, requestTimeoutMs: 10_000 };

/** The Web Lock every tab of this origin takes before rotating the refresh token. */
export const REFRESH_LOCK = 'cenox:auth-refresh';

/** The refresh itself: one POST, its session or null. Throws on a network error or abort. */
async function postRefresh(signal?: AbortSignal): Promise<AuthSession | null> {
  const response = await rawRequest(REFRESH_PATH, { method: 'POST', ...(signal ? { signal } : {}) });
  if (!response.ok) return null;
  return (await response.json()) as AuthSession;
}

/**
 * One rotation, taken in turn with every other tab.
 *
 * Tabs share one cookie jar, so if they refresh one at a time each request
 * carries the cookie the previous one rotated to, and the server never sees a
 * token twice. Without this, two tabs restoring at the same moment both send
 * the old cookie and the second trips reuse detection — which revokes the
 * whole family and signs out both. Nothing is shared between tabs except the
 * cookie the browser already shares: every tab rotates for itself.
 *
 * The lock is released only when the refresh REQUEST has settled — never
 * because a timer fired. A request that is still able to reach the server
 * while another tab sends the next one is the very race this exists to stop:
 * the late one arrives with a token the other has already spent, and the
 * family is revoked. So the request timeout aborts the fetch and then waits
 * for the fetch to actually finish; native fetch rejects as soon as it is
 * aborted, so in practice that is immediate. A fetch that ignored its abort
 * would keep the lock for as long as it lived, and the other tabs would give
 * up waiting (`lockWaitMs`) rather than overlap it.
 *
 * Where Web Locks do not exist, this is exactly the single-tab refresh the
 * client had before: no lock and no timeout.
 */
async function rotate(): Promise<AuthSession | null> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks) return postRefresh();

  const waiting = new AbortController();
  const waitTimer = setTimeout(() => waiting.abort(), REFRESH_TIMING.lockWaitMs);

  try {
    return await locks.request(
      REFRESH_LOCK,
      { mode: 'exclusive', signal: waiting.signal },
      async () => {
        clearTimeout(waitTimer);
        const request = new AbortController();
        const timer = setTimeout(() => request.abort(), REFRESH_TIMING.requestTimeoutMs);
        try {
          // Settles when the fetch does: answered, failed, or aborted.
          return await postRefresh(request.signal);
        } finally {
          clearTimeout(timer);
        }
      },
    );
  } finally {
    clearTimeout(waitTimer);
  }
}

/**
 * Refresh the session: one rotation, shared by everybody who asks.
 *
 * Concurrent callers await the same promise, so a page that fires six queries
 * on mount performs one rotation, not six — and so does restoring the session
 * on load, which React's StrictMode runs twice in development. Two refreshes
 * carrying the same cookie are exactly what the server's reuse detection
 * exists to catch: the second revokes the whole token family, the first's
 * fresh token with it, and the person is signed out by their own tab. Across
 * tabs, `rotate()` takes the same turn-by-turn approach with a Web Lock.
 *
 * Resolves to the session (truthy) or null. Never throws, and never goes
 * through `request()`, so a refresh that fails cannot try to refresh itself.
 */
export function refreshSession(): Promise<AuthSession | null> {
  refreshInFlight ??= (async () => {
    try {
      const session = await rotate();
      if (session) accessToken = session.accessToken;
      return session;
    } catch {
      return null;
    } finally {
      // Cleared on the next tick so callers already awaiting resolve first.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response = await rawRequest(path, options);

  // The refresh endpoint is never answered with a refresh, however it was
  // called: a 401 from it means the session is gone, and asking again would
  // only send the same dead cookie a second time.
  if (response.status === 401 && path !== REFRESH_PATH) {
    const refreshed = await refreshSession();
    if (refreshed) {
      response = await rawRequest(path, options);
    } else {
      accessToken = null;
      onUnauthenticated?.();
    }
  }

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';

  if (!response.ok) {
    let body: unknown = null;
    if (contentType.includes('application/json')) {
      body = await response.json().catch(() => null);
    }
    if (isApiErrorBody(body)) {
      const lost = response.status === 404 && body.error.message === 'Business not found'
        ? TENANT_PATH.exec(path)?.[1]
        : undefined;
      if (lost) onWorkspaceLost?.(lost);
      throw new ApiError(response.status, body.error);
    }
    throw new ApiError(response.status, {
      code: 'internal_error',
      message: `Request failed (${response.status})`,
    });
  }

  if (contentType.includes('application/json')) {
    return (await response.json()) as T;
  }
  return (await response.text()) as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  /** Replace a whole collection — the body IS the complete set, not a delta. */
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  /** A body is optional — some deletes carry a reason for the audit trail. */
  del: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', body }),
  /** multipart/form-data, for the spreadsheet import. */
  upload: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: 'POST', formData }),
};

/**
 * POST and read a server-sent event stream.
 *
 * Here rather than in the page, so a stream gets the same single-attempt
 * refresh on 401 that every other call gets — a chat answer must not fail
 * because a token expired between opening the tab and asking a question.
 *
 * `onEvent` is called for each event as it arrives. The promise resolves when
 * the stream ends. Aborting the signal stops reading; the server is told by
 * the socket closing, and decides for itself whether to finish the work (for
 * an AI answer it does, because the provider has already been paid).
 */
export async function streamEvents(
  path: string,
  body: unknown,
  onEvent: (event: string, data: unknown) => void,
  signal?: AbortSignal,
): Promise<void> {
  const send = (): Promise<Response> =>
    fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: {
        accept: 'text/event-stream',
        'content-type': 'application/json',
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      },
      credentials: 'include',
      body: JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });

  let response = await send();
  if (response.status === 401) {
    const refreshed = await refreshSession();
    if (!refreshed) {
      accessToken = null;
      onUnauthenticated?.();
      throw new ApiError(401, { code: 'unauthorized', message: 'Your session has ended.' });
    }
    response = await send();
  }

  if (!response.ok || !response.body) {
    // A refusal before the stream starts arrives as ordinary JSON.
    const parsed = (await response.json().catch(() => null)) as ApiErrorBody | null;
    throw new ApiError(
      response.status,
      parsed && isApiErrorBody(parsed)
        ? parsed.error
        : { code: 'internal_error', message: 'The assistant could not be reached.' },
    );
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffered = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffered += decoder.decode(value, { stream: true });

    /*
      Events are separated by a blank line, and a network chunk can end
      mid-event — so whatever follows the last blank line stays buffered
      rather than being parsed as truncated JSON.
    */
    const blocks = buffered.split('\n\n');
    buffered = blocks.pop() ?? '';

    for (const block of blocks) {
      const name = block.match(/^event: (.*)$/m)?.[1];
      const data = block.match(/^data: (.*)$/m)?.[1];
      if (!name || data === undefined) continue;
      try {
        onEvent(name, JSON.parse(data));
      } catch {
        // A malformed event is the server's problem; losing the rest of the
        // answer over it would be ours.
      }
    }
  }
}

/**
 * Upload one multipart body and report how much of it has gone.
 *
 * XMLHttpRequest rather than fetch, because fetch still cannot tell you how
 * far an upload has got — and a progress bar that only knows "started" and
 * "finished" is a spinner. Otherwise it behaves like `api.upload`: the same
 * token, the same cookie, the same single refresh on a 401 (the body is sent
 * again, so progress restarts from nothing), and errors arrive as `ApiError`
 * carrying the server's own message.
 *
 * `onProgress` receives a fraction from 0 to 1.
 */
export async function uploadWithProgress<T>(
  path: string,
  formData: FormData,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<T> {
  const send = (): Promise<{ status: number; body: unknown }> =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${BASE}${path}`);
      xhr.setRequestHeader('accept', 'application/json');
      if (accessToken) xhr.setRequestHeader('authorization', `Bearer ${accessToken}`);
      // Required for the httpOnly refresh cookie, as in `rawRequest`.
      xhr.withCredentials = true;
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total);
      };
      xhr.onload = () => {
        let body: unknown = null;
        try {
          body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        } catch {
          body = null;
        }
        resolve({ status: xhr.status, body });
      };
      xhr.onerror = () =>
        reject(new ApiError(0, { code: 'internal_error', message: 'The upload did not reach the server.' }));
      xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));
      signal?.addEventListener('abort', () => xhr.abort(), { once: true });
      // No content-type of our own: the browser writes it, boundary included.
      xhr.send(formData);
    });

  let result = await send();
  if (result.status === 401) {
    if (await refreshSession()) {
      onProgress(0);
      result = await send();
    } else {
      accessToken = null;
      onUnauthenticated?.();
    }
  }

  if (result.status >= 200 && result.status < 300) {
    onProgress(1);
    return result.body as T;
  }
  if (isApiErrorBody(result.body)) throw new ApiError(result.status, result.body.error);
  throw new ApiError(result.status, {
    code: 'internal_error',
    // A proxy's 413 arrives before the API has seen the body, so without our JSON.
    message: result.status === 413 ? 'That file is too large to upload.' : `Upload failed (${result.status})`,
  });
}

/**
 * Download a file (Excel/CSV export).
 *
 * The browser cannot attach the Authorization header to a plain link, so the
 * file is fetched, turned into a blob and saved through a synthetic anchor.
 */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  let response = await rawRequest(path);

  if (response.status === 401) {
    if (await refreshSession()) {
      response = await rawRequest(path);
    } else {
      onUnauthenticated?.();
      return;
    }
  }

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    if (isApiErrorBody(body)) throw new ApiError(response.status, body.error);
    throw new ApiError(response.status, { code: 'internal_error', message: 'Download failed' });
  }

  // Prefer the server's filename; it carries the dataset and date stamp.
  const disposition = response.headers.get('content-disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? fallbackName;

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * Fetch an authenticated file as a blob, to show in the page.
 *
 * The same reason as `downloadFile`: an <img src> cannot carry the
 * Authorization header, so the bytes are fetched here and handed to the page
 * as an object URL. Errors arrive as `ApiError`, so a missing file and a
 * missing permission read differently on screen.
 */
export async function fetchBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const options: RequestOptions = signal ? { signal } : {};
  let response = await rawRequest(path, options);
  if (response.status === 401 && (await refreshSession())) {
    response = await rawRequest(path, options);
  }
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    if (isApiErrorBody(body)) throw new ApiError(response.status, body.error);
    throw new ApiError(response.status, {
      code: 'internal_error',
      message: `Request failed (${response.status})`,
    });
  }
  return response.blob();
}

/** Build a query string, dropping empty values so URLs stay readable. */
export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const result = search.toString();
  return result ? `?${result}` : '';
}
